'use client';

import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RadioGroup, RadioGroupItem } from '@pickleballinc/ui/components/ui/radio-group';
import { cn } from '@pickleballinc/ui/lib/utils';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import {
	MatchCompletedEnum,
	MatchStatusApiMap,
	MatchStatusEnum,
	MatchupCompletedEnumTypes,
	MatchupStatusEnum,
	getMatchupStatusApiValue
} from '@/entities/matchup/constants';
import { Match } from '@/entities/matchup/Matchup';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onCompleteClick: (open: boolean) => void;
	matchupId: string;
	incompleteMatches: Match[];
}

export const CompleteTieSelectModal = ({ isOpen, onClose, onCompleteClick, matchupId, incompleteMatches }: Props) => {
	const [selection, setSelection] = useState<'Play' | 'Skip'>('Play');
	const [isInitialized, setIsInitialized] = useState(false);
	const modalDataRef = useRef<Match[]>([]);

	const updateMatchupMutation = useUpdateMatchup();
	const updateMatchMutation = useUpdateMatchMutation({ matchupId });

	if (isOpen && !isInitialized) {
		modalDataRef.current = incompleteMatches;
		setIsInitialized(true);
	}

	if (!isOpen && isInitialized) {
		setIsInitialized(false);
	}

	const handleCompleteWithoutTiebreaker = async (): Promise<void> => {
		const snapshot = modalDataRef.current;
		try {
			// Update all incomplete matches in parallel and wait for all to complete
			await Promise.all(
				snapshot.map((match) =>
					updateMatchMutation.mutateAsync({
						matchId: match.matchUuid,
						updateData: {
							matchStatus: MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS],
							matchCompletedType: MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
						}
					})
				)
			);

			// After all matches are successfully updated, update the matchup status
			await updateMatchupMutation.mutateAsync({
				matchup_uuid: matchupId,
				matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS),
				matchup_completed_type: MatchupCompletedEnumTypes.PLAYED_MATCHUP_COMPLETION_TYPE
			});

			toast.success('All matches completed successfully!');
			onClose();
		} catch (_error) {
			toast.error('Failed to complete matches. Please try again.');
			onClose();
		}
	};

	if (!isOpen) return null;

	const isPending = updateMatchMutation.isPending || updateMatchupMutation.isPending;

	return (
		<ModalWrapper title="Complete Matches" onClose={onClose} className="max-w-[520px]" closeOnOutsideClick={!isPending}>
			<hr className="text-gray-400 my-4" />
			<div className="flex flex-col gap-4 px-2">
				<div className="text-base font-medium text-gray-900">Are you sure you want to complete all matches?</div>

				<RadioGroup
					className="flex flex-col gap-4"
					value={selection}
					onValueChange={(value) => setSelection(value as 'Play' | 'Skip')}
					name="tiebreaker"
				>
					<div className="flex items-center">
						<RadioGroupItem
							id="play"
							value="Play"
							className="size-5 cursor-pointer"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />}
						/>
						<label className="text-md ml-2 cursor-pointer" htmlFor="play">
							Start the tiebreaker
						</label>
					</div>

					<div className="flex items-center">
						<RadioGroupItem
							id="skip"
							value="Skip"
							className="size-5 cursor-pointer"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faCircle} className={cn('!size-2', className)} />}
						/>
						<label className="text-md ml-2 cursor-pointer" htmlFor="skip">
							Complete matchup without playing tiebreaker
							<br />
							<span className="text-gray-600">The winner will be determined by the PD%</span>
						</label>
					</div>
				</RadioGroup>

				<div className="pt-2">
					<p className="text-sm text-gray-600">All incomplete matches will be marked as canceled and the matchup will be completed.</p>
				</div>

				<ActionButtons
					className="flex justify-end gap-3 pt-4"
					closeButtonClassName="w-32"
					submitButtonClassName="w-32"
					onClose={() => {
						if (!isPending) onClose();
					}}
					onSubmit={() => {
						if (selection === 'Skip') {
							handleCompleteWithoutTiebreaker();
						} else {
							onClose();
							onCompleteClick(true);
						}
					}}
					submitButtonText={isPending ? 'Completing...' : 'Complete'}
					closeButtonText="Cancel"
					submitButtonDisabled={isPending}
				/>
			</div>
		</ModalWrapper>
	);
};
